/**
 * Display settings — personal to this device, like the card layout (layout/model.ts).
 *
 * Unlike the layout, these are a flat key/value bag rather than per-column-count
 * arrangements: there's no screen-size dimension to them, just "how much of a note do you
 * want to see before it's a click to see more."
 *
 * `useSettings` exists because a change here now has somewhere else to reach: `DisplayCard`
 * used to be the only reader of its own stepper values, but a portable card can land on any
 * page (D111), including one that also has Task or Notes cards on it. `loadSettings()` alone
 * is a plain function call — nothing tells `TaskRow`/`NoteRow` to re-render when it changes,
 * so a stepper moved on the same page would not visibly do anything until something *else*
 * happened to redraw them. `useSyncExternalStore` is exactly this shape: a snapshot getter
 * plus a subscribe function, which is what `saveSetting` calling every listener gives it.
 */

import { useSyncExternalStore } from "react";

export type AppSettings = {
  /** Lines of a task's notes shown under its title before the eye icon offers to expand it.
   * 0 hides it entirely. */
  taskNotesLines: number;
  /** Lines of a note's body shown in the Notes card before the eye icon offers to expand it.
   * 0 hides it entirely. */
  noteBodyLines: number;
  /**
   * Planning ⇄ Execute — the density switch for Task and Routine cards (`CardModeToggle`,
   * in the nav). "plan" shows every button inline and every badge; "execute" collapses
   * Edit/Split/Drop/Snooze (Task rows) and Edit/Delete/History (Routine rows) into a kebab
   * menu, and hides the lower-priority badges (Estimate, Chosen, Quiet, Seen, Pushed,
   * Snoozed) down to just Energy/Important/Starred/Area/Due.
   *
   * Global rather than per-card, on purpose — cards are portable across pages (D111), so
   * there is no single "this card's own toggle" that would reach all of them, and the
   * whole point is one flip covering everything at once. Still lives here rather than a
   * separate store: it is exactly the same kind of thing the two settings above already
   * are, a personal-to-this-device display preference, just boolean-shaped instead of a
   * line count.
   */
  cardMode: "plan" | "execute";
  /**
   * Colorize Tasks — a muted blurred-ellipse glow on each task row, purely decorative
   * (Energy/Overdue/Important are already shown as explicit badges everywhere; this adds
   * nothing new to read, just visual distinctness between rows). Three independent
   * switches rather than one, since a reader might want the corners lit without also
   * duplicating Energy's own die-and-pip color language. All default off.
   */
  colorizeEnergy: boolean;
  colorizeOverdue: boolean;
  colorizeImportant: boolean;
  /**
   * Whether a filed Inbox AI retry clears itself, and after how many seconds
   * (`InboxAiRetryButton`). A retry's answer ("Created Task: …") holds the card open so the
   * person can see where the item went; this is how long it waits before dismissing it. Two
   * settings rather than one number with 0 meaning off: turning it off and back on should
   * return to the duration that was chosen, not to zero.
   */
  inboxAiAutoClear: boolean;
  inboxAiAutoClearSeconds: number;
};

export const SETTINGS_MIN = 0;
export const SETTINGS_MAX = 50;

const DEFAULTS: AppSettings = {
  taskNotesLines: 2,
  noteBodyLines: 5,
  cardMode: "plan",
  colorizeEnergy: false,
  colorizeOverdue: false,
  colorizeImportant: false,
  inboxAiAutoClear: true,
  inboxAiAutoClearSeconds: 8,
};

const STORAGE_KEY = "efss.settings";

function read(): Partial<AppSettings> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<AppSettings>) : {};
  } catch {
    // Private browsing, disabled storage, or corrupted JSON. Defaults are a fine outcome.
    return {};
  }
}

// `useSyncExternalStore` compares this function's return value across calls with
// `Object.is` to decide whether a subscriber needs to re-render — a plain `{ ...DEFAULTS,
// ...read() }` would return a *new* object every call, identical contents or not, so every
// render looks like a change, triggers another render, and never settles ("Maximum update
// depth exceeded"). Reading fresh every time (so an external change — a test's
// `localStorage.clear()`, this module living across more than one test in the same file —
// is never missed) but only minting a new object when a value actually differs keeps the
// reference stable in between, which is what a snapshot getter has to do.
let cached: AppSettings = { ...DEFAULTS, ...read() };

export function loadSettings(): AppSettings {
  const next = { ...DEFAULTS, ...read() };
  const changed = (Object.keys(next) as (keyof AppSettings)[]).some(
    (key) => next[key] !== cached[key],
  );
  if (changed) cached = next;
  return cached;
}

const listeners = new Set<() => void>();

export function saveSetting<K extends keyof AppSettings>(key: K, value: AppSettings[K]): void {
  // The clamp only means something for the numeric line-count settings; `cardMode` is a
  // string and has no range to clamp into — `Math.round` on it would just produce `NaN`.
  const toStore =
    typeof value === "number"
      ? (Math.max(SETTINGS_MIN, Math.min(SETTINGS_MAX, Math.round(value))) as AppSettings[K])
      : value;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...read(), [key]: toStore }));
  } catch {
    /* ignore — the setting simply will not persist */
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Live settings — re-renders the caller whenever `saveSetting` changes anything, wherever
 * that happens to be called from. Use this instead of `loadSettings()` directly in anything
 * that stays mounted (a task row, a note row); a one-off read at event time (`DisplayCard`'s
 * own steppers, which only ever read their starting value once) can keep using
 * `loadSettings()` — there is nothing to subscribe to when nothing re-renders in response. */
export function useSettings(): AppSettings {
  return useSyncExternalStore(subscribe, loadSettings);
}
